import {
  Box,
  Disclaimer,
  Div,
  H3,
  H5,
  OutstandingBalance,
  SaasProfitability,
  formatBankAccount,
  formatPaymentCard,
  P,
  Separator,
  displayCentsInDollars,
  formatSaasPeriod,
  lightTheme,
} from '@vgrubs/components';
import {
  formatDay,
  formatDayTime,
  relativeDays,
} from '../helpers/receivablesLabels';
import { StatusBadge } from './ReceivablesCells';

type Props = {
  current: OutstandingBalance;
};

/**
 * Where one location stands right now: the vDrive reserve used, the fees for
 * being below zero and the 3 days before vDrive pauses, the old vDrive items
 * only admin sees, SaaS (and whether the owner is shown it), and the bank
 * account.
 */
export const BalanceOverview = ({ current }: Props) => {
  const { vDrive, saas } = current;
  const oldItems = vDrive?.itemsTotal ?? 0;

  return (
    <Box $flex $column $gap="12px">
      <Div $flex $alignCenter $between $gap="16px">
        <Div>
          <Disclaimer $m="0">Total owed</Disclaimer>
          {/* Already includes the old vDrive items here: on admin, `total`
              is the reserve used plus every open item. */}
          <H3
            $m="0"
            $color={current.total > 0 ? lightTheme.primary : undefined}
          >
            {displayCentsInDollars(current.total)}
          </H3>
        </Div>
        <Disclaimer
          $m="0"
          $right
          $color={
            current.achMandateValid ? lightTheme.green : lightTheme.primary
          }
        >
          {current.achMandateValid
            ? formatBankAccount(current.bankAccount) ?? 'Bank account active'
            : 'No active bank account'}
        </Disclaimer>
      </Div>

      <Separator />

      <Div $flex $alignCenter $between $gap="16px">
        <H5 $m="0">vDrive</H5>
        {vDrive?.negativePaused && (
          <StatusBadge color={lightTheme.primary}>
            Paused (3 days negative)
          </StatusBadge>
        )}
      </Div>
      {vDrive ? (
        <>
          <Line
            label="Negative balance"
            value={displayCentsInDollars(vDrive.negativeBalance)}
            strong={vDrive.negativeBalance > 0}
          />
          {!!vDrive.negativeFeeDue && (
            <Line
              label="Negative balance fees"
              value={displayCentsInDollars(vDrive.negativeFeeDue)}
              strong
            />
          )}
          {!!vDrive.negativeFeeTonight && (
            <Line
              label="Fee added tonight if still negative"
              value={displayCentsInDollars(vDrive.negativeFeeTonight)}
            />
          )}
          {vDrive.negativeSince && (
            <Line
              label="Negative since"
              value={`${formatDayTime(vDrive.negativeSince)} (${relativeDays(
                vDrive.negativeSince
              )})`}
            />
          )}
          {vDrive.negativePauseAt && (
            <Line
              label={vDrive.negativePaused ? 'Paused on' : 'Pauses on'}
              value={`${formatDayTime(vDrive.negativePauseAt)} (${relativeDays(
                vDrive.negativePauseAt
              )})`}
            />
          )}
          <Line
            label="Reserve left"
            value={`${displayCentsInDollars(
              vDrive.remainingOverdraft
            )} of ${displayCentsInDollars(vDrive.overdraftLimit)}`}
          />
          {oldItems > 0 && (
            <Line
              label="Previous Transaction Due Balance (admin only)"
              value={displayCentsInDollars(oldItems)}
              strong
            />
          )}
          {vDrive.limitReached && (
            <P $m="0" $color={lightTheme.primary}>
              The reserve limit is used up: new deliveries are refused.
            </P>
          )}
          {vDrive.negativePaused && (
            <P $m="0" $color={lightTheme.primary}>
              The balance has been negative for more than 3 days: new deliveries
              are refused until the negative balance and its fees are paid. A
              bank payment on its way does not resume them.
            </P>
          )}
          <Line
            label="Primary card"
            value={formatPaymentCard(vDrive.card) ?? 'No own card'}
          />
          {vDrive.secondaryCard && (
            <Line
              label="Secondary card"
              value={formatPaymentCard(vDrive.secondaryCard) ?? 'Added'}
            />
          )}
          {vDrive.primaryCardFailed && (
            <P $m="0" $color={lightTheme.orange}>
              The primary card is declining; the secondary card is covering the
              charges.
            </P>
          )}
          {vDrive.cardFailed && (
            <Div>
              <P $m="0" $color={lightTheme.primary}>
                No card on file could be charged.
              </P>
              {/* Stays on after the vGrubs balance paid a top-up: the card
                  itself still has to be fixed. */}
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                The next 2 top-ups try it again, then deliveries skip it (the
                daily retry still tries it) until it is replaced. Top-ups it
                does not cover come from the location&apos;s vGrubs balance,
                then the bank.
              </Disclaimer>
            </Div>
          )}
          {vDrive.vDriveCardSunset && (
            <P $m="0">
              Still on the vGrubs vDrive card,{' '}
              {vDrive.vDriveCardSunset.ended
                ? `which stopped being charged on ${formatDay(
                    vDrive.vDriveCardSunset.sunsetAt
                  )}`
                : `which stops being charged ${relativeDays(
                    vDrive.vDriveCardSunset.sunsetAt
                  )} (${formatDay(vDrive.vDriveCardSunset.sunsetAt)})`}
              .{' '}
              {displayCentsInDollars(vDrive.vDriveCardSunset.remainingBalance)}{' '}
              left on it.
            </P>
          )}
        </>
      ) : (
        <Disclaimer $m="-8px 0 0 0" $color={lightTheme.lightGray}>
          This location has no vDrive.
        </Disclaimer>
      )}

      <Separator />

      <H5 $m="0">SaaS</H5>
      {saas ? (
        <>
          <Line
            label="Unpaid"
            value={displayCentsInDollars(saas.total)}
            strong={saas.total > 0}
          />
          <SaasPeriods saas={saas} />
          {saas.pendingBank.count > 0 && (
            <Line
              label="On its way by bank"
              value={displayCentsInDollars(saas.pendingBank.total)}
            />
          )}
        </>
      ) : (
        <Disclaimer $m="-8px 0 0 0" $color={lightTheme.lightGray}>
          No SaaS debt.
        </Disclaimer>
      )}

      {/* Only worth reading while there is SaaS debt to show or hide. */}
      {!!saas?.total && current.saasProfitability && (
        <SaasProfitabilityLines profitability={current.saasProfitability} />
      )}
    </Box>
  );
};

/**
 * The unpaid months behind the SaaS total, oldest first, and whatever has no
 * month (charges added by hand, very old ones) as "Other charges". Nothing
 * when the backend sends no periods.
 */
const SaasPeriods = ({
  saas,
}: {
  saas: NonNullable<OutstandingBalance['saas']>;
}) => {
  const periods = saas.periods ?? [];
  if (periods.length === 0) return null;
  const other =
    saas.total - periods.reduce((sum, period) => sum + period.amount, 0);

  return (
    <Div $flex $column $gap="4px" $mt="-8">
      {periods.map((period) => (
        <Div key={period.from} $flex $alignCenter $between $gap="16px">
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            {formatSaasPeriod(period.from, period.to)}
          </Disclaimer>
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            {displayCentsInDollars(period.amount)}
          </Disclaimer>
        </Div>
      ))}
      {other > 0 && (
        <Div $flex $alignCenter $between $gap="16px">
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            Other charges
          </Disclaimer>
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            {displayCentsInDollars(other)}
          </Disclaimer>
        </Div>
      )}
    </Div>
  );
};

type LineProps = {
  label: string;
  value: string;
  strong?: boolean;
};

const Line = ({ label, value, strong }: LineProps) => (
  <Div $flex $alignCenter $between $gap="16px">
    <P $m="0">{label}</P>
    <P $m="0" $bold={strong} $color={strong ? lightTheme.primary : undefined}>
      {value}
    </P>
  </Div>
);

/**
 * Why the owner does or does not see the SaaS debt: vGrubs has to have been
 * worth it to them over the window (the backend decides, this only shows its
 * numbers). Hidden means nobody but support will bring the debt up.
 */
const SaasProfitabilityLines = ({
  profitability,
}: {
  profitability: SaasProfitability;
}) => (
  <>
    <Disclaimer
      $m="0"
      $color={profitability.profitable ? lightTheme.green : lightTheme.orange}
    >
      {profitability.profitable
        ? 'Shown to the owner: vGrubs paid off for them'
        : 'Hidden from the owner: vGrubs did not pay off enough for them'}{' '}
      over the last {profitability.windowDays} days.
    </Disclaimer>
    <Line
      label="Recovered orders"
      value={displayCentsInDollars(profitability.recoveries)}
    />
    <Line
      label="Virtual brand revenue"
      value={displayCentsInDollars(profitability.virtualRevenue)}
    />
    <Line
      label="vDrive savings"
      value={displayCentsInDollars(profitability.vDriveSavings)}
    />
    <Line label="Together" value={displayCentsInDollars(profitability.total)} />
  </>
);
